import React, { FC, useEffect, useContext, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import { Button } from "@natera/form";
import { DateRange as RangeModifier } from "@natera/material/lib/date";

import { routes } from "@app/routing";
import { DateRange, Pagination, SpinnerView } from "@app/components";
import { BlueHeaderContent } from "@app/components/ui/layout";
import { PostBillHistoryContext, UppAuthContext } from "@app/provider";
import { SORT_BY } from "@app/postBill/provider/postbillHistoryProvider";
import SortIcon from "@assets/svg/icons/sort.svg";

import "./billingHistoryTemplate.scss";

const messages = defineMessages({
  billingHistoryNewest: {
    id: "billingHistoryNewest",
    defaultMessage: "Newest",
  },
  billingHistoryOldest: {
    id: "billingHistoryOldest",
    defaultMessage: "Oldest",
  },
});

interface BillingHistoryTemplateProps {
  title: string;
  children: ReactNode;
  loadHistory: (from?: Date, to?: Date, page?: number) => Promise<void>;
  loadPage: (page?: number, from?: Date, to?: Date) => Promise<void>;
  toggleSort: (from?: Date, to?: Date) => void;
  /** Constrains the header and body to a 600px medium width. */
  medium?: boolean;
}

const BillingHistoryTemplate: FC<BillingHistoryTemplateProps> = ({
  title,
  children,
  loadHistory,
  loadPage,
  toggleSort,
  medium = false,
}) => {
  const intl = useIntl();
  const {
    isLoading,
    testCards,
    setTestCards,
    sortBy,
    pageNum,
    reset,
    currentPage,
  } = useContext(PostBillHistoryContext);
  const { profile: uppProfile } = useContext(UppAuthContext);
  const navigate = useNavigate();

  const [rangeDate, setRangeDate] = React.useState<RangeModifier>();
  const getRangeDate = (date$?: RangeModifier) => setRangeDate(date$);

  useEffect(() => {
    if (uppProfile?.patientUID && !isLoading) {
      loadHistory();
    }
  }, [uppProfile?.patientUID]);

  useEffect(() => {
    if (testCards && rangeDate?.from && rangeDate.to) {
      setTestCards([]);
      loadHistory(rangeDate.from, rangeDate.to);
    }
  }, [rangeDate]);

  const renderSorting = () => {
    toggleSort(rangeDate?.from, rangeDate?.to);
  };

  const renderOrderBtn = () => {
    const btnText =
      sortBy === SORT_BY.NEWEST
        ? intl.formatMessage(messages.billingHistoryNewest)
        : intl.formatMessage(messages.billingHistoryOldest);
    return (
      <Button icon={SortIcon} onClick={renderSorting}>
        {btnText}
      </Button>
    );
  };

  const renderPagination = () => {
    return (
      <Pagination
        total={pageNum}
        currentPage={currentPage}
        handlePage={loadPage}
        rangeDate={rangeDate}
      />
    );
  };

  const handleBack = () => {
    navigate(routes.billingCenter, {
      state: { from: routes.billingHistoryPayments },
    });
  };

  return (
    <BlueHeaderContent
      blueHeaderTitle={title}
      backArrowHeaderButton={handleBack}
      medium={medium}
      noGradient
    >
      <div className="billing-history">
        {<SpinnerView isLoading={isLoading} />}
        <div className="billing-history__content">
          <div className="billing-history__page">
            <div className="billing-history__filters">
              <DateRange
                rangeDate={getRangeDate}
                reset={reset}
                getHistory={loadHistory}
              />
              {renderOrderBtn()}
            </div>
            <div className="billing-history__cards">{children}</div>
            <div className="billing-history__pagination">
              {renderPagination()}
            </div>
          </div>
        </div>
      </div>
    </BlueHeaderContent>
  );
};

export default BillingHistoryTemplate;
